import {
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, ChevronsUpDown, Plus, Search } from "lucide-react";
import { Avatar } from "../Avatar/Avatar";
import { Input } from "../Input/Input";
import { Popover } from "../Popover/Popover";
import styles from "./AppSelector.module.css";

interface Project {
  id: string;
  name: string;
  has_icon?: boolean;
}

interface Props {
  projects: Project[];
  activeId?: string;
  onSelect?: (id: string) => void;
  /** When set, the popup gets a "+ New project…" footer that calls this. */
  onCreateNew?: () => void;
  /** Show ⌘N kbd hint in the footer. Defaults to `true` when `onCreateNew` is set. */
  showShortcut?: boolean;
}

export function AppSelector({
  projects,
  activeId,
  onSelect,
  onCreateNew,
  showShortcut,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const triggerBtnRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const rowRefs = useRef<Array<HTMLElement | null>>([]);

  const hasFooter = !!onCreateNew;
  const kbdEnabled = hasFooter && showShortcut !== false;
  const isEmpty = projects.length === 0;
  // Single-project, non-switchable: render a non-interactive div.
  const isStatic = projects.length === 1 && !hasFooter;

  const active =
    projects.find((p) => p.id === activeId) ??
    (projects.length === 1 ? projects[0] : undefined);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [projects, query]);

  const totalSlots = filtered.length + (hasFooter ? 1 : 0);
  const footerIndex = hasFooter ? filtered.length : -1;

  // Reset row-ref cache each render.
  rowRefs.current = [];

  // When closed: reset query + active index.
  useEffect(() => {
    if (open) return;
    setQuery("");
    setActiveIndex(0);
  }, [open]);

  // When the filter changes, keep activeIndex in range.
  useEffect(() => {
    setActiveIndex((idx) => {
      if (totalSlots === 0) return 0;
      return idx >= totalSlots ? 0 : idx;
    });
  }, [totalSlots]);

  // Focus the search input on open (defer one tick — Popover mounts via portal).
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Scroll the active row into view (guarded for environments like jsdom
  // that don't implement scrollIntoView).
  useEffect(() => {
    if (!open) return;
    const el = rowRefs.current[activeIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  // Cmd/Ctrl+N while open → trigger onCreateNew.
  useEffect(() => {
    if (!open || !onCreateNew) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        onCreateNew?.();
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCreateNew]);

  function handleTriggerClick() {
    if (isEmpty) {
      // Empty state: invoke onCreateNew directly (no popup).
      onCreateNew?.();
      return;
    }
    if (isStatic) return;
    setOpen((o) => !o);
  }

  function commitActive() {
    if (totalSlots === 0) return;
    if (hasFooter && activeIndex === footerIndex) {
      onCreateNew?.();
      setOpen(false);
      return;
    }
    const p = filtered[activeIndex];
    if (p) {
      onSelect?.(p.id);
      setOpen(false);
    }
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLElement>) {
    if (totalSlots === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % totalSlots);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + totalSlots) % totalSlots);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitActive();
    }
  }

  // ───── Trigger ──────────────────────────────────────────────────────
  const triggerClass = [
    styles.trigger,
    open ? styles.open : "",
    isStatic ? styles.static : "",
  ]
    .filter(Boolean)
    .join(" ");

  const subText = isEmpty
    ? "Create your first one"
    : projects.length === 1
      ? "Project"
      : `Project · ${projects.length} total`;

  // No active project: either there are none at all (isEmpty) or several
  // exist but the current route isn't scoped to one — say so instead of
  // rendering a blank title.
  const titleText = isEmpty
    ? "No project"
    : active?.name ?? "Select a project";

  const triggerInner = (
    <>
      {active ? (
        <Avatar name={active.name} size={28} gradient />
      ) : (
        <span className={styles.emptyIcon} aria-hidden>
          —
        </span>
      )}
      <span className={styles.triggerText}>
        <span className={styles.triggerTitle}>{titleText}</span>
        <span
          className={`${styles.triggerSub}${
            isEmpty ? ` ${styles.triggerSubAccent}` : ""
          }`}
        >
          {subText}
        </span>
      </span>
      {!isStatic && !isEmpty && (
        <ChevronsUpDown size={14} aria-hidden className={styles.chevron} />
      )}
    </>
  );

  return (
    <div className={styles.root}>
      {isStatic ? (
        <div className={triggerClass} aria-label="Current project">
          {triggerInner}
        </div>
      ) : (
        <button
          ref={triggerBtnRef}
          type="button"
          className={triggerClass}
          onClick={handleTriggerClick}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Switch project"
        >
          {triggerInner}
        </button>
      )}

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerBtnRef}
        placement="bottom-start"
        offset={6}
        width={280}
      >
        <div onKeyDown={handleKeyDown}>
          <div className={styles.searchRow}>
            <Input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Search projects…"
              leadingIcon={<Search size={14} aria-hidden />}
              aria-label="Search projects"
            />
          </div>

          <div className={styles.groupLabel}>
            Your projects ({filtered.length})
          </div>

          {filtered.length === 0 ? (
            <div className={styles.empty}>
              No matches for &quot;{query}&quot;
            </div>
          ) : (
            <ul className={styles.list} role="listbox" aria-label="Projects">
              {filtered.map((p, i) => {
                const isCurrent = p.id === activeId;
                const isRowActive = i === activeIndex;
                const rowClass = [
                  styles.row,
                  isRowActive ? styles.active : "",
                  isCurrent ? styles.current : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <li key={p.id} style={{ listStyle: "none" }}>
                    <button
                      ref={(el) => {
                        rowRefs.current[i] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      tabIndex={-1}
                      className={rowClass}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => {
                        onSelect?.(p.id);
                        setOpen(false);
                      }}
                    >
                      <Avatar name={p.name} size={24} />
                      <span className={styles.rowName}>{p.name}</span>
                      {isCurrent && (
                        <Check
                          size={14}
                          aria-hidden
                          data-testid="appselector-check"
                          className={styles.check}
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {hasFooter && (
            <div className={styles.footer}>
              <button
                ref={(el) => {
                  rowRefs.current[footerIndex] = el;
                }}
                type="button"
                tabIndex={-1}
                className={`${styles.footerRow}${
                  activeIndex === footerIndex ? ` ${styles.active}` : ""
                }`}
                onMouseEnter={() => setActiveIndex(footerIndex)}
                onClick={() => {
                  onCreateNew?.();
                  setOpen(false);
                }}
              >
                <span className={styles.footerIcon} aria-hidden>
                  <Plus size={14} />
                </span>
                <span className={styles.footerLabel}>New project…</span>
                {kbdEnabled && (
                  <span className={styles.kbd} aria-hidden>
                    &#8984;N
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
